/**
 * Utility functions for date formatting
 * Designed to work with international users without forcing regional formats
 */

/**
 * Formats a date string or Date object using user's locale
 * @param date - Date string (ISO format) or Date object
 * @param options - Optional formatting options
 * @returns Formatted date string or "Invalid date" if invalid
 */
export const formatDate = (
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) {
    return 'Invalid date';
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date provided to formatDate:', date);
      return 'Invalid date';
    }

    const defaultOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    // Use user's locale instead of forcing pt-BR
    return dateObj.toLocaleDateString(undefined, options || defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error, 'Date:', date);
    return 'Invalid date';
  }
};

/**
 * Formats a date to simple format (respects user's locale)
 * @param date - Date string (ISO format) or Date object
 * @returns Formatted date string or "Invalid date" if invalid
 */
export const formatSimpleDate = (date: Date | string | null | undefined): string => {
  return formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formats a date with time (respects user's locale)
 * @param date - Date string (ISO format) or Date object
 * @returns Formatted date string or "Invalid date" if invalid
 */
export const formatDateTime = (date: Date | string | null | undefined): string => {
  return formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formats time only (respects user's locale)
 * @param date - Date string (ISO format) or Date object
 * @returns Formatted time string or "Invalid time" if invalid
 */
export const formatTime = (date: Date | string | null | undefined): string => {
  if (!date) {
    return 'Invalid time';
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid time';
    }

    // Use user's locale instead of forcing pt-BR
    return dateObj.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};